import * as React from "react";
import { useState, useEffect } from "react";
import OfficeAddinMessageBar from "./OfficeAddinMessageBar";
import { useAuth0 } from "@auth0/auth0-react";
import BaseComponent from "./BaseComponent";
import LoginComponent from "./Authentication/LoginComponents";
import { AlertProvider } from "./MessageAlert/AlertContext";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./Redux/store";
import { clearMatters } from "./Redux/matterSlice";
import { getTokenByProfileEmail, getStorageItem, SetStorageItem } from "../helpers/helpers";
import { registerSilentRefresh, setAccessTokenDirect, getAccessToken as getManagedAccessToken, getSessionHandle, setSessionHandle, setRecoveryUserId, getRecoveryUserId } from "../services/tokenManager";
import { Box } from "@mui/material";
import Loader from "./MessageAlert/Loader";
import { Widgets } from "@mui/icons-material";
import {LOCALSTORAGE_KEYS } from '../utils/Constants';
import { API_BASE_URL } from "../config/config";
interface AppProps {
	title: string;
	isOfficeInitialized: boolean;
}

const App: React.FC<AppProps> = () => {
	const { isAuthenticated } = useAuth0();
	const [accessToken, setAccessToken] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [emailConnected, setEmailConnected] = useState("");
	const [hasKeyAccess, setHasKeyAccess] = useState<boolean>(false);
	const [isLogged, setIsLogged] = useState(false);
	const [isEmail, setIsEmail] = useState(false);
	const [isShowLogin, setIsShowLogin] = useState(false)
	const [isShowBase, setIsShowBase] = useState(false)
	const [isLoading, setIsLoading] = useState(true)

	const dispatch = useDispatch<AppDispatch>();
	const matters = useSelector((state: RootState) => state.matters.matters);
	const { getAccessTokenSilently, getIdTokenClaims, logout, } = useAuth0();


	const handleAuth = (status: boolean) => {
		setIsLogged(status);
		setHasKeyAccess(true)
		setIsShowLogin(false)
		setIsShowBase(true)
	};

	

	const handleClear = () => {
		dispatch(clearMatters());
		
	};
	const handleSingOUt = (data: boolean) => {
		handleClear()
		setHasKeyAccess(data);
		setIsShowLogin(true)
		setIsShowBase(false)
		
	}
	
const handleClose = () => {
   window.location.reload();
   
  };
	useEffect(() => {
		// Lets tokenManager silently renew this (modern-host, auth0-react-managed)
		// session's access token without needing a server-brokered session
		// handle - see tokenManager.registerSilentRefresh / setAccessTokenDirect.
		registerSilentRefresh(async () => {
			try {
				return await getAccessTokenSilently();
			} catch (err: any) {
				// Do NOT swallow this. When Auth0 refuses a refresh token to the
				// browser-side exchange, auth0-spa-js throws `missing_refresh_token`
				// here. Silently returning null made that indistinguishable from an
				// ordinary logged-out state, which is why this was invisible from
				// the client and only showed up in the Auth0 tenant log.
				console.error(
					"[App] Silent token renewal failed:",
					err?.error || err?.message || err,
					err
				);
				return null;
			}
		});

		Office.onReady(async(info) => {
			try {

				handleClear()
				window.addEventListener("beforeunload", () => {
			    SetStorageItem(LOCALSTORAGE_KEYS.mode, "0");
					// Save state or cleanup here
				});
				if (Office.addin && Office.addin.onVisibilityModeChanged) {
					Office.addin.onVisibilityModeChanged((args) => {
						if (args.visibilityMode === Office.VisibilityMode.hidden) {
							 console.log("Task Pane is closing...");
							SetStorageItem(LOCALSTORAGE_KEYS.mode, "0"); // Save state
						}
					});
				}

				if (
					info.host === Office.HostType.Outlook ||
					info.platform === Office.PlatformType.OfficeOnline
				) {
					const item = Office.context.mailbox.item;

					const userProfilr = Office.context.mailbox.userProfile;
					const profileEmail = userProfilr.emailAddress

					const fetchToken = async () => {
					try {
						// Step 1: Check tokenManager FIRST: for the classic-Outlook (Mac/Win
						// desktop) login path, the session lives only there (an
						// opaque handle in sessionStorage, refreshable via the
						// Public API) - it survives this exact task-pane reload,
						// but getAccessTokenSilently() below can never see it, since
						// auth0-react has no active session for that login path.
						const managedToken = await getManagedAccessToken();
						if (managedToken) {
							console.log("[App.tsx] Token recovered from session handle (task pane or roamingSettings)");
							return managedToken;
						}

						// MARKER: START-SESSION-PERSISTENCE-FIX
						// Step 2: Try recovery for classic Outlook via stored user ID
						const recoveryUserId = getRecoveryUserId();
						console.log("[App.tsx] DEBUG: recoveryUserId =", recoveryUserId);
						console.log("[App.tsx] DEBUG: getSessionHandle() =", getSessionHandle());

						if (recoveryUserId && !getSessionHandle()) {
							try {
								const url = `${API_BASE_URL}AuthSession/recover-by-token`;
								console.log("[App.tsx] Attempting session recovery via user ID...");
								console.log("[App.tsx] POST URL:", url);
								console.log("[App.tsx] Request body:", { userId: recoveryUserId });

								const recoveryResponse = await fetch(url, {
									method: "POST",
									headers: { "Content-Type": "application/json" },
									body: JSON.stringify({ userId: recoveryUserId, accessToken : managedToken}),
								});

								console.log("[App.tsx] Recovery response status:", recoveryResponse.status);

								if (recoveryResponse.ok) {
									const recoveryData = await recoveryResponse.json();
									console.log("[App.tsx] Recovery response data:", recoveryData);
									setSessionHandle(recoveryData.sessionHandle);
									setAccessTokenDirect(recoveryData.accessToken);
									console.log("[App.tsx] ✓ Session recovered via user ID");
									return recoveryData.accessToken;
								} else {
									const errorText = await recoveryResponse.text();
									console.log("[App.tsx] User ID recovery failed with status:", recoveryResponse.status, "Error:", errorText);
								}
							} catch (err) {
								console.log("[App.tsx] User ID recovery attempt failed:", err);
							}
						} else {
							console.log("[App.tsx] Skipping user ID recovery: recoveryUserId found =", !!recoveryUserId, ", session handle empty =", !getSessionHandle());
						}
						// MARKER: END-SESSION-PERSISTENCE-FIX

						// Step 3: Try Auth0 SDK (modern host path)
						const token = await getAccessTokenSilently();

						// Step 4: Got access token but NO session handle (modern host path)
						if (token && !getSessionHandle()) {
							try {
								console.log("[App.tsx] Attempting session recovery via access token (modern host)...");
								const recoveryResponse = await fetch(`${API_BASE_URL}AuthSession/recover`, {
									method: "POST",
									headers: { "Content-Type": "application/json" },
									body: JSON.stringify({ accessToken: token }),
								});

								if (recoveryResponse.ok) {
									const recoveryData = await recoveryResponse.json();
									setSessionHandle(recoveryData.sessionHandle);
									setAccessTokenDirect(recoveryData.accessToken);
									console.log("[App.tsx] ✓ Session recovered via access token");
									return recoveryData.accessToken;
								}
							} catch (err) {
								console.log("[App.tsx] Access token recovery failed:", err);
							}
						}

						return token;
					} catch (err) {
						console.warn("Error fetching token", err);
						return null;
					}
					};

					let doseExistToken = await fetchToken();
					//let doseExistToken = getTokenByProfileEmail(profileEmail)
					if (!doseExistToken) {
 						 doseExistToken = await fetchToken();
						}
					if (doseExistToken) {
						localStorage.setItem(LOCALSTORAGE_KEYS.LoggedProfile, profileEmail);
						setAccessTokenDirect(doseExistToken);
						setHasKeyAccess(true)
						setIsShowBase(true)
					} else {
						setHasKeyAccess(false)
						setIsShowLogin(true)
					}
				}
			} catch (error) {
				console.error("Error initializing Office.js:", error);
			}
		})
		setTimeout(() => {

			setIsLoading(false)
		}, 500)
	}, [])
	return (
		<div style={{ backgroundColor: "white", minHeight: "100vh", width: "100%" }}>
			<AlertProvider>
				{errorMessage && (
					<OfficeAddinMessageBar
						onDismiss={() => setErrorMessage("")}
						message={errorMessage}
					/>
				)}
				
				{isLoading && <div>
					<Box
						sx={{
							position: "fixed",
							top: 0,
							left: 0,
							width: "100%",
							height: "100%",
							zIndex: 1300,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: "rgba(255, 255, 255, 1)",
						}}
					>
						{/* Wrapper for vertical alignment */}
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
							<Loader />
							{/* <div style={{ marginTop: "16px", fontSize: "16px", color: "#555" }}>
								Loading, please wait...
							</div> */}
						</Box>
					</Box>
				</div>
				}


				{isShowLogin && <LoginComponent handleAuth={handleAuth} />}
				{isShowBase &&
					<BaseComponent
						handleSingOUt={handleSingOUt}
					/>
				}
			
				{!isShowLogin && !isLoading && !isShowBase &&  <div style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100vh',  // Full height for perfect vertical centering
						textAlign: 'center'
					}}>
						<div style={{ 
								marginBottom: '20px',
								fontSize: '18px',       // Increased font size ✅
								fontWeight: '500'       // Slightly bolder for readability
							}}>Fetching email</div>
						<button
							type="button"
							className="px-6 py-2 border rounded-lg hover:bg-gray-50"
							onClick={handleClose}>
							Click here to reload
						</button>
					</div>
				}
			</AlertProvider>
		</div>
	);
};

export default App;
